import PageLoader from '@components/PageLoader'
import { useAdminOnboarding } from '@hooks/useAdminOnboarding'
import { useAdminFilter } from '@pages/Admin/AdminFilterContext'
import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { AdminHeading } from '../AdminLayout.styles'
import {
	DeletedTag,
	InternalTag,
	PageButton,
	Pagination,
	PageStatus,
	Row,
	SearchInput,
	Table,
	TableWrap,
	Td,
	Th,
	Toolbar,
} from '../adminList.styles'
import { formatDate, formatDateTime, formatName } from '../adminFormat'
import { rangeLabel } from '../dateRange'
import DistributionBars from './DistributionBars'
import { formatIntents, formatShare } from './onboardingView'
import {
	CohortNote,
	CoverageGrid,
	DistributionGrid,
	Section,
	SectionSubtitle,
	SectionTitle,
	Tile,
	TileCount,
	TileLabel,
	TileShare,
	WarningStrip,
} from './OnboardingPage.styles'
import { EmptyNote } from '@components/EmptyState'
import { ErrorNote } from '@components/Notice'

const PAGE_SIZE = 25

/**
 * Admin onboarding survey view: how much of a signup cohort answered the
 * two-question survey, and what they said.
 *
 * The cohort is every account *created* in the global date range, so the
 * denominator matches the Users page — a user who signed up before the range and
 * answered inside it is deliberately not counted. Search narrows only the
 * per-user table; the tiles, platform split and distributions keep describing the
 * whole cohort, so typing a name is a lookup rather than a re-scoping.
 */
const OnboardingPage: React.FC = () => {
	const navigate = useNavigate()
	const { from, to } = useAdminFilter()
	const [searchInput, setSearchInput] = useState('')
	const [debouncedQ, setDebouncedQ] = useState('')
	const [page, setPage] = useState(1)

	// Debounce the search box so we refetch on a pause, not on every keystroke.
	useEffect(() => {
		const timer = setTimeout(() => setDebouncedQ(searchInput.trim()), 300)
		return () => clearTimeout(timer)
	}, [searchInput])

	// Any new filter (search term or date range) starts the result set over at page 1.
	useEffect(() => {
		setPage(1)
	}, [debouncedQ, from, to])

	const { data, isLoading, isError } = useAdminOnboarding({
		page,
		pageSize: PAGE_SIZE,
		q: debouncedQ,
	})

	if (isLoading) {
		return (
			<>
				<AdminHeading>Onboarding</AdminHeading>
				<PageLoader label="Loading onboarding answers…" />
			</>
		)
	}

	if (isError || !data) {
		return (
			<>
				<AdminHeading>Onboarding</AdminHeading>
				<ErrorNote>Could not load onboarding answers. Please try again.</ErrorNote>
			</>
		)
	}

	const { cohortSize, coverage } = data

	return (
		<>
			<AdminHeading>Onboarding</AdminHeading>

			{data.unattributed.count > 0 && (
				<WarningStrip role="status">
					<span aria-hidden="true">⚠</span>
					<span>
						{data.unattributed.count.toLocaleString()} onboarding{' '}
						{data.unattributed.count === 1 ? 'answer' : 'answers'} in this range{' '}
						{data.unattributed.count === 1 ? 'has' : 'have'} no user id
						{data.unattributed.platforms.length > 0 &&
							` (${data.unattributed.platforms.join(', ')})`}{' '}
						and {data.unattributed.count === 1 ? 'is' : 'are'} excluded from every number below.
					</span>
				</WarningStrip>
			)}

			<CohortNote>
				{cohortSize.toLocaleString()} {cohortSize === 1 ? 'user' : 'users'} signed up ·{' '}
				{rangeLabel(from, to)}
			</CohortNote>

			<CoverageGrid>
				<Tile>
					<TileLabel>Answered intent</TileLabel>
					<TileCount>{coverage.intent.toLocaleString()}</TileCount>
					<TileShare>{formatShare(coverage.intent, cohortSize)}</TileShare>
				</Tile>
				<Tile>
					<TileLabel>Answered discovery</TileLabel>
					<TileCount>{coverage.discoverySource.toLocaleString()}</TileCount>
					<TileShare>{formatShare(coverage.discoverySource, cohortSize)}</TileShare>
				</Tile>
				<Tile>
					<TileLabel>Answered both</TileLabel>
					<TileCount>{coverage.both.toLocaleString()}</TileCount>
					<TileShare>{formatShare(coverage.both, cohortSize)}</TileShare>
				</Tile>
				<Tile>
					<TileLabel>Answered neither</TileLabel>
					<TileCount>{coverage.neither.toLocaleString()}</TileCount>
					<TileShare>{formatShare(coverage.neither, cohortSize)}</TileShare>
				</Tile>
			</CoverageGrid>

			<Section>
				<SectionTitle>By platform</SectionTitle>
				<SectionSubtitle>
					A platform answering far below the others usually means its survey never fired.
				</SectionSubtitle>
				<TableWrap>
					<Table>
						<thead>
							<tr>
								<Th>Platform</Th>
								<Th>Users</Th>
								<Th>Answered intent</Th>
								<Th>Answered discovery</Th>
							</tr>
						</thead>
						<tbody>
							{data.platforms.length === 0 ? (
								<tr>
									<Td colSpan={4}>
										<EmptyNote>No users in this range.</EmptyNote>
									</Td>
								</tr>
							) : (
								data.platforms.map(row => (
									<tr key={row.platform}>
										<Td>{row.platform}</Td>
										<Td>{row.users.toLocaleString()}</Td>
										<Td>
											{row.answeredIntent.toLocaleString()} ·{' '}
											{formatShare(row.answeredIntent, row.users)}
										</Td>
										<Td>
											{row.answeredDiscoverySource.toLocaleString()} ·{' '}
											{formatShare(row.answeredDiscoverySource, row.users)}
										</Td>
									</tr>
								))
							)}
						</tbody>
					</Table>
				</TableWrap>
			</Section>

			<DistributionGrid>
				<DistributionBars
					title="Intent"
					subtitle={`Multi-select · share of the ${data.intent.answered.toLocaleString()} who answered`}
					distribution={data.intent}
				/>
				<DistributionBars
					title="Discovery source"
					subtitle={`Share of the ${data.discoverySource.answered.toLocaleString()} who answered`}
					distribution={data.discoverySource}
				/>
			</DistributionGrid>

			<Toolbar>
				<SearchInput
					type="search"
					aria-label="Search answers by name or email"
					placeholder="Search by name or email…"
					value={searchInput}
					onChange={e => setSearchInput(e.target.value)}
				/>
			</Toolbar>

			<TableWrap>
				<Table>
					<thead>
						<tr>
							<Th>Name</Th>
							<Th>Email</Th>
							<Th>Platform</Th>
							<Th>Intents</Th>
							<Th>Discovery source</Th>
							<Th>Signed up</Th>
							<Th>Answered</Th>
						</tr>
					</thead>
					<tbody>
						{data.answers.length === 0 ? (
							<tr>
								<Td colSpan={7}>
									<EmptyNote>No users match this filter.</EmptyNote>
								</Td>
							</tr>
						) : (
							data.answers.map(answer => (
								<Row
									key={answer.userId}
									$deleted={answer.isDeleted}
									onClick={() => navigate(`/admin/users/${answer.userId}`)}
								>
									<Td>
										{formatName(answer.firstName, answer.lastName)}
										{answer.isInternal && <InternalTag>Internal</InternalTag>}
										{answer.isDeleted && <DeletedTag>Deleted</DeletedTag>}
									</Td>
									<Td>{answer.email}</Td>
									<Td>{answer.platform}</Td>
									<Td>{formatIntents(answer.intents)}</Td>
									<Td>{answer.discoverySource ?? '—'}</Td>
									<Td>{formatDate(answer.signupDate)}</Td>
									<Td>{answer.answeredAt === null ? '—' : formatDateTime(answer.answeredAt)}</Td>
								</Row>
							))
						)}
					</tbody>
				</Table>
			</TableWrap>

			<Pagination>
				<PageStatus>
					Page {data.page} of {data.totalPages} · {data.total.toLocaleString()}{' '}
					{data.total === 1 ? 'user' : 'users'}
				</PageStatus>
				<PageButton disabled={data.page <= 1} onClick={() => setPage(p => Math.max(1, p - 1))}>
					Previous
				</PageButton>
				<PageButton disabled={data.page >= data.totalPages} onClick={() => setPage(p => p + 1)}>
					Next
				</PageButton>
			</Pagination>
		</>
	)
}

export default OnboardingPage
